import { RpcError, RpcRedirect } from "@buildpy/api/client";
import { Button } from "@buildpy/ui/button";
import Notice from "@buildpy/ui/notice";
import { isNotFound, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

import NotFound from "@/app/not-found";
import { logout } from "@/api/actions/org";
import { isAuthUnavailableError } from "@/api/client";
import { OrgBlockedNotice, OrgLinkUnavailableNotice, orgAccessFailureOf } from "@/components/org-access-notice";
import { leaveOnUnauthorized, leavesOnUnauthorized } from "@/lib/redirect-error";
import OrgRejectedScreen from "@/modules/onboarding/ui/org-rejected-screen";
import AppError from "@/pages/app/error";

import { withRouteRetry } from "./route-retry";

export const AUTH_UNAVAILABLE_TITLE = "로그인 서버가 잠시 응답하지 않습니다";
export const AUTH_UNAVAILABLE_BODY = "잠시 뒤 다시 시도해 주세요.";

// 인증 서버 장애(503 auth_unavailable) 안내 — core 의 SIGN_IN_MESSAGE 는 서버 모듈(lib/supabase)이라 브라우저 번들에 못 들여 web 고정 문구를 쓴다.
export const AuthUnavailableNotice = ({ onRetry }: { onRetry?: () => void }) => (
  <Notice
    actions={
      onRetry && (
        <Button size="sm" variant="primary" onClick={onRetry}>
          다시 시도
        </Button>
      )
    }
    body={AUTH_UNAVAILABLE_BODY}
    className="max-w-md"
    title={AUTH_UNAVAILABLE_TITLE}
  />
);

// 라우트 오류 경계 — 404·라우터 신호와 렌더 중 읽기 오류를 받는다 · 401 은 settleRedirect 와 같은 규칙(회원 화면이면 세션을 지우고 /login · 로그인 없는 화면이면 오류 화면 — 공개 읽기 p:·s:·c: 의 401 은 loadView 가 그대로 여기로 던진다).
const RouteErrorView = ({ error, reset }: ErrorComponentProps) => {
  const toLogin = error instanceof RpcError && error.status === 401 && leavesOnUnauthorized(error);
  useEffect(() => {
    if (toLogin) leaveOnUnauthorized(error);
  }, [toLogin, error]);

  if (error instanceof RpcRedirect || toLogin) return null;
  if (isAuthUnavailableError(error)) return <AuthUnavailableNotice onRetry={reset} />;
  const org = orgAccessFailureOf(error);
  if (org?.kind === "blocked" && org.rejected) return <OrgRejectedScreen reason={org.rejectReason} onLogout={logout} />;
  if (org?.kind === "blocked") return <OrgBlockedNotice status={org.status} onLogout={logout} />;
  if (org?.kind === "unavailable") return <OrgLinkUnavailableNotice />;
  if (isNotFound(error) || (error instanceof RpcError && error.status === 404)) return <NotFound />;
  return <AppError error={error as Error & { digest?: string }} reset={reset} />;
};

const RouteError = withRouteRetry(RouteErrorView);

export default RouteError;
